import { HttpErrorResponse } from '@angular/common/http';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createFeature, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import {
    createAnnotation,
    deleteAnnotation,
    getTaskAnnotations,
    setAnnotations,
    setPageNumber,
    updateAnnotation,
} from '../actions/annotations.actions';
import { ANNOTATIONS_FEATURE_KEY } from '../constants/constants';
import { Annotation } from '../interfaces/annotation.interface';

export interface AnnotationsState extends EntityState<Annotation> {
    taskAnnotations: Annotation[];
    limit: number;
    page: number;
    totalCount: number;
    error: string | null | HttpErrorResponse;
}

export interface AppState {
    aumicoUsersAnnotations: AnnotationsState;
}

// Entity adapter
export const annotationAdapter = createEntityAdapter<Annotation>({
    selectId: (annotation) => annotation.id,
    sortComparer: sortByCreatedAt,
});

export function sortByCreatedAt(a: Annotation, b: Annotation): number {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();

    if (dateA === dateB) {
        return a.id.localeCompare(b.id);
    }

    return dateA - dateB;
}

export const selectFeature = createFeatureSelector<AnnotationsState>(ANNOTATIONS_FEATURE_KEY);

export const { selectAll: selectTaskAnnotations } = annotationAdapter.getSelectors(selectFeature);

export const getAllTaskAnnotations = (state: AnnotationsState) => state.taskAnnotations;

export const getPageNumber = (state: AnnotationsState) => state.page;
export const selectPageNumber = createSelector(selectFeature, getPageNumber);

export const initialAnnotationsState: AnnotationsState = annotationAdapter.getInitialState({
    taskAnnotations: [],
    limit: 50,
    page: 1,
    totalCount: 0,
    error: null,
});

export const annotationsReducer = createReducer(
    initialAnnotationsState,

    // Get Task Annotations
    on(getTaskAnnotations.do, (state) => ({
        ...state,
        error: undefined,
    })),

    on(getTaskAnnotations.success, (state, { payload }) => {
        // Prevent duplicates and append
        const existingIds = new Set(state.taskAnnotations.map((a) => a.id));
        const newAnnotations = payload.annotations.filter((a) => !existingIds.has(a.id));
        const updatedTaskAnnotations = [...state.taskAnnotations, ...newAnnotations];

        // First apply adapter to update normalized state
        const entityState = annotationAdapter.setAll(updatedTaskAnnotations, state);
        // Then merge in pagination metadata and list
        return {
            ...entityState,
            taskAnnotations: updatedTaskAnnotations,
            totalCount: payload.totalCount,
            error: null,
        };
    }),

    on(getTaskAnnotations.failure, (state, action) => ({ ...state, error: action.payload.message })),

    // Create Annotation
    on(createAnnotation.success, (state, { payload }) => annotationAdapter.addOne(payload, state)),
    on(createAnnotation.failure, (state, { payload }) => ({ ...state, error: payload.message })),

    // Update Annotation
    on(updateAnnotation.success, (state, { payload }) =>
        annotationAdapter.updateOne({ id: payload.id, changes: payload }, state),
    ),
    on(updateAnnotation.failure, (state, { payload }) => ({ ...state, error: payload.message })),

    // Delete Annotation
    on(deleteAnnotation.success, (state, { payload }) => annotationAdapter.removeOne(payload, state)),
    on(deleteAnnotation.failure, (state, { payload }) => ({ ...state, error: payload.message })),

    on(setAnnotations, (state, { payload }) => annotationAdapter.setAll(payload, state)),

    on(setPageNumber, (state, { payload }) => ({
        ...state,
        page: payload,
    })),
);

export const annotationsFeature = createFeature({
    name: 'annotations',
    reducer: annotationsReducer,
});

export function reducer(state: AnnotationsState | undefined, action: Action) {
    return annotationsReducer(state, action);
}

export const { selectLimit, selectError, selectTotalCount } = annotationsFeature;
